import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { Typography, Box, Breadcrumbs } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { SalaryComponent } from "./SalaryComponents";
import { SalaryTemplate } from "./SalaryTemplates";
import { AssignSalary } from "./AssignSalary";
import { themes, theme } from "services/constants";
import { useSelector, useDispatch } from "react-redux";
import { FilterProvider } from "pages/FilterContext";
import { setFilterApi } from "services/Redux/userManagament";
import { SalaryRevisionScreen } from "./SalaryRevision";
import { EmployeeSalaryScreen } from "./EmployeeSalaryInformation";
// Payroll > Salary is a sidebar flyout (see routes.js) and the only way to
// move between these screens; the old in-page tab list was removed. Each
// flyout item has its own URL under /admin/payroll/salary/..., matching this
// folder's sub-folders, and opens the matching screen below.
export const SALARY_BASE_PATH = "/admin/payroll/salary";

const SEGMENT_TO_ITEM = {
  "salary-components": "Salary Component",
  "salary-templates": "Salary Template",
  "assign-salary/salary-component": "Group Salary Component",
  "salary-revision": "Salary Revision",
  "employee-salary-information": "Salary Table",
};

// Every screen key, including the employee drill-downs the Assign screens
// switch to, belongs to exactly one URL.
const ITEM_TO_SEGMENT = {
  "Salary Component": "salary-components",
  "Salary Template": "salary-templates",
  "Group Salary Component": "assign-salary/salary-component",
  "Employee Salary Component": "assign-salary/salary-component",
  "Salary Revision": "salary-revision",
  "Salary Table": "employee-salary-information",
};

// Names from the new Payroll menu, shown in the breadcrumb.
const ITEM_LABEL = {
  "Salary Component": "Salary Components",
  "Salary Template": "Salary Templates",
  "Group Salary Component": "Assign Salary",
  "Employee Salary Component": "Assign Salary",
  "Salary Revision": "Salary Revision",
  "Salary Table": "Employee Salary",
};

// "assign-salary" has two screens below it, so it keeps two path parts.
const getSalarySegment = (pathname) => {
  const path = pathname.replace(/\/+$/, "");
  if (!path.startsWith(`${SALARY_BASE_PATH}/`)) return "";
  const parts = path.slice(SALARY_BASE_PATH.length + 1).toLowerCase().split("/");
  return parts[0] === "assign-salary" ? parts.slice(0, 2).join("/") : parts[0];
};

export const SalaryConfiguration = () => {
  const { pathname } = useLocation();
  const history = useHistory();
  const segment = getSalarySegment(pathname);
  const [salaryConfigItem, setSalaryConfigItem] = useState(
    SEGMENT_TO_ITEM[segment] || "Salary Component",
  );
  const { domain, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const dispatch = useDispatch();

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Salary Configuration",
  );

  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === salaryConfigItem,
  )?.sub_module_id;

  // A flyout click changes the URL; open the matching screen unless the
  // current one already belongs to that URL (e.g. the employee view of
  // Assign Salary stays open under its own URL).
  const salaryConfigItemRef = useRef(salaryConfigItem);
  salaryConfigItemRef.current = salaryConfigItem;
  useEffect(() => {
    const item = SEGMENT_TO_ITEM[segment];
    if (!item) return;
    if (ITEM_TO_SEGMENT[salaryConfigItemRef.current] === segment) return;
    setSalaryConfigItem(item);
    dispatch(setFilterApi(true));
  }, [segment, dispatch]);

  // Switches the screen and keeps the URL in step. Passed to the child
  // screens (they switch screens themselves, e.g. Back to Salary Component),
  // so it must stay stable: the segment is read from a ref, not a dependency.
  const segmentRef = useRef(segment);
  segmentRef.current = segment;
  const changeSalaryItem = useCallback(
    (itemTitle) => {
      setSalaryConfigItem(itemTitle);
      const nextSegment = ITEM_TO_SEGMENT[itemTitle];
      if (nextSegment && nextSegment !== segmentRef.current) {
        history.push(`${SALARY_BASE_PATH}/${nextSegment}`);
      }
    },
    [history],
  );

  const renderMainContent = () => {
    switch (salaryConfigItem) {
      case "Salary Component":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <SalaryComponent
              setSalaryConfigItem={changeSalaryItem}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Salary Template":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <SalaryTemplate
              setSalaryConfigItem={changeSalaryItem}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );

      case "Group Salary Component":
      case "Employee Salary Component":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <AssignSalary subModuleId={subModuleId} />
          </FilterProvider>
        );

      case "Salary Revision":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <SalaryRevisionScreen subModuleId={subModuleId} />
          </FilterProvider>
        );

      case "Salary Table":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <EmployeeSalaryScreen subModuleId={subModuleId} />
          </FilterProvider>
        );

      default:
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <SalaryComponent
              setSalaryConfigItem={changeSalaryItem}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );
    }
  };

  // Old /admin/payroll/salaryconfig links, the bare /admin/payroll/salary
  // and unknown paths land on Salary Components; a bare /assign-salary and
  // the removed Assign Salary Template URL land on Assign Salary.
  if (!SEGMENT_TO_ITEM[segment]) {
    const target = segment.startsWith("assign-salary")
      ? "assign-salary/salary-component"
      : "salary-components";
    return <Redirect to={`${SALARY_BASE_PATH}/${target}`} />;
  }

  // Redesigned screens draw their own page header and breadcrumb, so they
  // skip the old wrapper below.
  if (
    [
      "Salary Component",
      "Salary Template",
      "Group Salary Component",
      "Employee Salary Component",
      "Salary Revision",
      "Salary Table",
    ].includes(salaryConfigItem)
  ) {
    return renderMainContent();
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          height: "94.5vh",
          backgroundColor: themes?.whiteColor,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            minWidth: 0,
            backgroundColor: "#f5f5fd",
            transition: "all 0.3s ease",
          }}
        >
          <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
            <Typography>Payroll</Typography>
            <Typography>Salary</Typography>
            <Typography color={themes?.blueColor}>
              {ITEM_LABEL[salaryConfigItem] || salaryConfigItem}
            </Typography>
          </Breadcrumbs>

          {renderMainContent()}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
